/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 *   console.log(111)
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
$(document).ready(function(){
  $(".custom-color-swatch-js input").change(function(){
    $(this).parents(".swatch").addClass("selected");
    $(this).parents(".swatch").siblings().removeClass("selected");
    
    var selColor = $(this).val();
    $("[data-selected-color]").text(selColor);
  });

  initFontStyle();
  $("select.form-font-field-js").change(function(){
    var selFont = $(this).val();
    if(selFont != ""){
      var optionStyle = $(this).find("option[value='"+ selFont +"']").attr("style");
      if(selFont == "Font 2" || selFont == "Font 3" || selFont == "Font 17"){
        optionStyle += "; line-height: 60px;"
      }
      $(this).attr("style", optionStyle);
      
      $("select#selectFont").val(selFont).trigger("change");
       updateFontPreview();

    }
  }).trigger("change");
  
});

function initFontStyle(){
  if($(".form-font-field-js").length){
    var selectOptions = $(".form-font-field-js option");
  
    if($(".form-font-field-js option").attr("data-fontnumber") == undefined){
    selectOptions.eq(0).css("font-family", "Marker Felt");
    selectOptions.eq(1).css("font-family", "Hokey Pokey").css("font-size", "14px");
    selectOptions.eq(2).css("font-family", "Autumn in November");
    selectOptions.eq(3).css("font-family", "Personality DEMO");
    selectOptions.eq(4).css("font-family", "Cursive-Serif");
    selectOptions.eq(5).css("font-family", "Rose Bud ALP");
    selectOptions.eq(6).css("font-family", "Miss Magnolia Script").css("text-transform", "uppercase");
    selectOptions.eq(7).css("font-family", "Brusher");
    selectOptions.eq(8).css("font-family", "Sweet Pea");
    selectOptions.eq(9).css("font-family", "KG Strawberry Limeade");
    selectOptions.eq(10).css("font-family", "Legothick").css("text-transform", "uppercase");
    selectOptions.eq(11).css("font-family", "Cheri Liney");
    selectOptions.eq(12).css("font-family", "PN Smarty Pants");
    selectOptions.eq(13).css("font-family", "Magnolia Sky");  
    selectOptions.eq(14).css("font-family", "Jenna Sue");
    selectOptions.eq(15).css("font-family", "Apple Pie");
    selectOptions.eq(16).css("font-family", "Madina Clean");
    selectOptions.eq(17).css("font-family", "Alameda");
    selectOptions.eq(18).css("font-family", "Donut Derby");
    selectOptions.eq(19).css("font-family", "Stez Sans");
    selectOptions.eq(20).css("font-family", "WillowMarket-Regular");
    selectOptions.eq(21).css("font-family", "Camica");
    selectOptions.eq(22).css("font-family", "Rosefield-Regular");
    selectOptions.eq(23).css("font-family", "ItalianTypewriter-Bold");
    selectOptions.eq(24).css("font-family", "DingDong-Regular");
    
    }else{
      $(".form-font-field-js option[data-fontnumber='1']").css("font-family", "Marker Felt");
    $(".form-font-field-js option[data-fontnumber='2']").css("font-family", "Hokey Pokey").css("font-size", "14px");
    $(".form-font-field-js option[data-fontnumber='3']").css("font-family", "Autumn in November");
    $(".form-font-field-js option[data-fontnumber='4']").css("font-family", "Personality DEMO");
    $(".form-font-field-js option[data-fontnumber='5']").css("font-family", "Cursive-Serif");
    $(".form-font-field-js option[data-fontnumber='6']").css("font-family", "Rose Bud ALP");
    $(".form-font-field-js option[data-fontnumber='7']").css("font-family", "Miss Magnolia Script").css("text-transform", "uppercase");
    $(".form-font-field-js option[data-fontnumber='8']").css("font-family", "Brusher");
    $(".form-font-field-js option[data-fontnumber='9']").css("font-family", "Sweet Pea");
    $(".form-font-field-js option[data-fontnumber='10']").css("font-family", "KG Strawberry Limeade");
    $(".form-font-field-js option[data-fontnumber='11']").css("font-family", "Legothick").css("text-transform", "uppercase");
    $(".form-font-field-js option[data-fontnumber='12']").css("font-family", "Cheri Liney");
    $(".form-font-field-js option[data-fontnumber='13']").css("font-family", "PN Smarty Pants");
    $(".form-font-field-js option[data-fontnumber='14']").css("font-family", "Magnolia Sky");  
    $(".form-font-field-js option[data-fontnumber='15']").css("font-family", "Jenna Sue");
    $(".form-font-field-js option[data-fontnumber='16']").css("font-family", "Apple Pie");
    $(".form-font-field-js option[data-fontnumber='17']").css("font-family", "Madina Clean");
    $(".form-font-field-js option[data-fontnumber='18']").css("font-family", "Alameda");
    $(".form-font-field-js option[data-fontnumber='19']").css("font-family", "Donut Derby");
    $(".form-font-field-js option[data-fontnumber='20']").css("font-family", "Stez Sans");
    $(".form-font-field-js option[data-fontnumber='21']").css("font-family", "WillowMarket-Regular");
    $(".form-font-field-js option[data-fontnumber='22']").css("font-family", "Camica");
    $(".form-font-field-js option[data-fontnumber='23']").css("font-family", "Rosefield-Regular");
    $(".form-font-field-js option[data-fontnumber='24']").css("font-family", "ItalianTypewriter-Bold");
    $(".form-font-field-js option[data-fontnumber='25']").css("font-family", "DingDong-Regular");
    
    
    }

    

  }
}

window.addToCartCallBack = function(item, items) {
  // Custom logic after item addition
  // console.log(11111, items);
  var isParcelProtectionAdded = false;
  $(items).each(function(i, eitem){
    if(eitem.id == 40555778146347){
      isParcelProtectionAdded = true;
    }
  });
  if(!isParcelProtectionAdded){
    addParcelProtection();
  }

};

function addParcelProtection(){
  // Get variant ID and quantity
  const id = 40555778146347;
  const quantity = 1;
  
  // Manually add item with custom logic
  window.monster_addToCart({ 
    id,
    quantity
  });
  
  return false; // Always prevent default handling in this example
}


// If product contains note field then make it required
$(document).ready(function(){

  if($(".js-product-form .custom-font-textarea").length){

    $(".js-product-form button.add-to-cart").attr("disabled", "disabled");
    $(".js-product-form .custom-font-textarea").on("change click paste keydown keyup", function(){
      var noteText = $.trim($(this).val());
      if(noteText == ""){
        $(".js-product-form button.add-to-cart").attr("disabled", "disabled");
      }else{
        $(".js-product-form button.add-to-cart").removeAttr("disabled");
        $(".seller-note-error").remove();
      }
    });


    setInterval(function(){
      var noteText = $.trim($(".js-product-form .custom-font-textarea").val());
      if(noteText == ""){
        $(".js-product-form button.add-to-cart").attr("disabled", "disabled");
      }else{
        $(".js-product-form button.add-to-cart").removeAttr("disabled");
      }
    },500);

     $(".js-product-form .quantity-submit-row__submit").click(function(){
      $(".seller-note-error").remove();
      if($(this).find("button.add-to-cart").attr("disabled") == "disabled"){
        console.log("Prevent Add to cart if seller note blank");
        $(".js-product-form .custom-font-textarea").after('<p class="seller-note-error">Please add seller note to add this product.</p>');
      }
     });
  }
});